// Single source of truth for the 80/60/40/20/0 score-tier palette used by
// donuts, badges, dots, legends, and table cells across the dashboards. Same
// breakpoints everywhere — only the rendering surface (hex vs Tailwind class)
// differs. Ordered high → low; `min` is the inclusive lower bound of the band.

// `tintHex` is the pale 100-level fill (table-cell backgrounds); `hex` is the
// saturated 500-level mark color (dots, gradient, donut stroke).
export const SCORE_TIERS = [
  { min: 80, hex: "#10b981", tintHex: "#d1fae5", textClass: "text-emerald-600", dotClass: "bg-emerald-500", badgeClass: "bg-emerald-100 text-emerald-700", glowClass: "bg-emerald-500", strokeClass: "text-emerald-500" },
  { min: 60, hex: "#84cc16", tintHex: "#ecfccb", textClass: "text-lime-600", dotClass: "bg-lime-500", badgeClass: "bg-lime-100 text-lime-700", glowClass: "bg-lime-500", strokeClass: "text-lime-500" },
  { min: 40, hex: "#f59e0b", tintHex: "#fef3c7", textClass: "text-amber-600", dotClass: "bg-amber-500", badgeClass: "bg-amber-100 text-amber-700", glowClass: "bg-amber-500", strokeClass: "text-amber-500" },
  { min: 20, hex: "#f97316", tintHex: "#ffedd5", textClass: "text-orange-600", dotClass: "bg-orange-500", badgeClass: "bg-orange-100 text-orange-700", glowClass: "bg-orange-500", strokeClass: "text-orange-500" },
  { min: 0, hex: "#f43f5e", tintHex: "#ffe4e6", textClass: "text-rose-600", dotClass: "bg-rose-500", badgeClass: "bg-rose-100 text-rose-700", glowClass: "bg-rose-500", strokeClass: "text-rose-500" },
];

const tierFor = (score) =>
  SCORE_TIERS.find((tier) => score >= tier.min) ||
  SCORE_TIERS[SCORE_TIERS.length - 1];

// The tier object for a score, or null for a null score — lets surfaces with
// bespoke class needs (glow, stroke, badge) still derive from the one table.
export const scoreTier = (score) => (score == null ? null : tierFor(score));

export const scoreHex = (score) =>
  score == null ? "#cbd5e1" : tierFor(score).hex;

// Pale band fill for table-cell backgrounds; slate for no-data.
export const scoreTint = (score) =>
  score == null ? "#f1f5f9" : tierFor(score).tintHex;

export const scoreTextClass = (score) =>
  score == null ? "text-slate-400" : tierFor(score).textClass;

// Tier list low → high with display labels ("<20%", "20–39%", …, "80%+") for
// score legends.
export const scoreTierLegend = () => {
  const ordered = [...SCORE_TIERS].sort((a, b) => a.min - b.min);
  return ordered.map((tier, i) => {
    const next = ordered[i + 1];
    let label;
    if (!next) label = `${tier.min}%+`;
    else if (tier.min === 0) label = `<${next.min}%`;
    else label = `${tier.min}–${next.min - 1}%`;
    return { ...tier, label };
  });
};
